<script setup>
import { computed, ref } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import {
  MessagesSquare, HelpCircle, Send, Phone, MessageCircle, Mail, MapPin, Clock, Info,
} from 'lucide-vue-next'
import PageHeader from '../Components/PageHeader.vue'
import Card from '../Components/Card.vue'
import MessageThread from '../Components/MessageThread.vue'
import FaqList from '../Components/FaqList.vue'

const props = defineProps({
  aircraft: { type: Object, required: true },
  team: { type: Object, required: true },
  subjects: { type: Array, required: true },
  channels: { type: Array, required: true },
  messages: { type: Array, required: true },
  faq: { type: Array, required: true },
})

const CHANNEL_ICON = {
  phone: Phone,
  whatsapp: MessageCircle,
  email: Mail,
  address: MapPin,
}

const page = usePage()
const clientName = computed(() => page.props.client?.name ?? '')

// Os CTAs das outras telas chegam com ?assunto=financeiro|documento e já
// deixam o assunto escolhido.
const initialSubject = new URLSearchParams(window.location.search).get('assunto')
const subject = ref(
  props.subjects.some((s) => s.id === initialSubject) ? initialSubject : 'manutencao',
)

// A mensagem é gravada no servidor; a conversa vem das props e se atualiza
// sozinha na resposta do Inertia.
const form = useForm({ subject: subject.value, text: '' })
const canSend = computed(() => form.text.trim().length > 0 && !form.processing)

const send = () => {
  if (!canSend.value) return

  form.subject = subject.value
  form.post('/suporte/mensagens', {
    preserveScroll: true,
    onSuccess: () => form.reset('text'),
  })
}
</script>

<template>
  <Head title="Suporte" />

  <div class="space-y-6">
    <PageHeader title="SUPORTE" :aircraft="aircraft" />

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div class="space-y-6 xl:col-span-2">
        <Card title="Conversa com a equipe" :icon="MessagesSquare">
          <MessageThread :messages="messages" :client-name="clientName" />

          <form class="mt-6 border-t border-slate-100 pt-5" @submit.prevent="send">
            <fieldset>
              <legend class="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                Assunto
              </legend>
              <div class="mt-2 flex flex-wrap gap-2">
                <label
                  v-for="item in subjects"
                  :key="item.id"
                  class="cursor-pointer rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
                  :class="subject === item.id
                    ? 'bg-up-navy text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
                >
                  <input v-model="subject" type="radio" :value="item.id" name="assunto" class="sr-only" />
                  {{ item.label }}
                </label>
              </div>
            </fieldset>

            <label for="mensagem" class="sr-only">Sua mensagem</label>
            <textarea
              id="mensagem"
              v-model="form.text"
              rows="3"
              placeholder="Escreva sua mensagem para a equipe técnica…"
              class="mt-4 w-full resize-y rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-up-navy placeholder:text-slate-400 focus:border-up-orange focus:outline-none"
            />

            <p v-if="form.errors.text" role="alert" class="mt-2 text-sm text-up-orange-dark">
              {{ form.errors.text }}
            </p>

            <div class="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                :disabled="!canSend"
                class="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold tracking-wide uppercase transition-colors"
                :class="canSend
                  ? 'bg-up-orange text-white hover:bg-up-orange-dark'
                  : 'cursor-not-allowed bg-slate-200 text-slate-400'"
              >
                <Send class="h-4 w-4" aria-hidden="true" />
                {{ form.processing ? 'Enviando…' : 'Enviar mensagem' }}
              </button>
              <p class="text-xs text-slate-500">{{ team.response_time }}</p>
            </div>
          </form>
        </Card>

        <Card title="Dúvidas frequentes" :icon="HelpCircle">
          <FaqList :items="faq" />
        </Card>
      </div>

      <div class="space-y-6">
        <Card title="Atendimento" :icon="Clock">
          <div class="flex items-center gap-3 rounded-lg bg-up-bg px-4 py-3">
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-up-navy text-xs font-bold text-white"
              aria-hidden="true"
            >UP</span>
            <div class="min-w-0">
              <p class="font-semibold text-up-navy">{{ team.name }}</p>
              <p class="truncate text-xs text-slate-500">{{ team.role }}</p>
            </div>
          </div>
          <p class="mt-4 flex items-center gap-2 text-sm text-slate-600">
            <Clock class="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />{{ team.hours }}
          </p>
        </Card>

        <Card title="Outros canais" :icon="Phone">
          <ul class="divide-y divide-slate-100">
            <li v-for="channel in channels" :key="channel.kind" class="flex items-start gap-3 py-3">
              <component
                :is="CHANNEL_ICON[channel.kind]"
                class="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                aria-hidden="true"
              />
              <div class="min-w-0">
                <p class="text-xs text-slate-500">{{ channel.label }}</p>
                <p class="text-sm font-medium break-words text-up-navy">{{ channel.value }}</p>
              </div>
            </li>
          </ul>
        </Card>

        <Card>
          <p class="flex gap-2 text-sm leading-relaxed text-slate-600">
            <Info class="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            <span>
              Para assuntos financeiros, as instruções de pagamento continuam sendo enviadas por
              e-mail pelo financeiro — o portal não processa pagamentos.
            </span>
          </p>
        </Card>
      </div>
    </div>
  </div>
</template>
